<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>管理员</span>
        <div class="filters">
          <el-input v-model="username" placeholder="用户名" clearable style="width: 180px" @keyup.enter.native="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
          </el-input>
          <el-button type="primary" size="small" @click="openDialog()">新增</el-button>
        </div>
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="username" label="用户名" min-width="120" />
        <el-table-column prop="realName" label="姓名" min-width="100" />
        <el-table-column label="角色" min-width="180">
          <template slot-scope="{ row }">
            <el-tag v-for="name in roleNamesOf(row)" :key="name" size="mini" class="role-tag">{{ name }}</el-tag>
            <span v-if="roleNamesOf(row).length === 0" class="muted">未分配</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90">
          <template slot-scope="{ row }">{{ labelOf(COMMON_STATUS, row.status) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="140" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDialog(row)">编辑</el-button>
            <el-button type="text" size="small" class="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </el-card>

    <el-dialog :title="form.id ? '编辑管理员' : '新增管理员'" :visible.sync="visible" width="460px">
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" :disabled="!!form.id" />
        </el-form-item>
        <el-form-item label="姓名">
          <el-input v-model="form.realName" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="form.phone" placeholder="选填" />
        </el-form-item>
        <el-form-item label="角色" prop="roleIds">
          <el-select v-model="form.roleIds" multiple filterable placeholder="请选择角色" style="width: 100%">
            <el-option v-for="role in roles" :key="role.id" :label="role.roleName || role.roleCode" :value="role.id" />
          </el-select>
          <div class="form-tip">未分配角色的账号登录后看不到任何菜单，也无法调用后台接口。</div>
        </el-form-item>
        <el-form-item :label="form.id ? '新密码' : '密码'" :prop="form.id ? '' : 'password'">
          <el-input v-model="form.password" type="password" :placeholder="form.id ? '留空则不修改' : '请输入密码'" show-password />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 100%">
            <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAdmins, saveAdmin, deleteAdmin, getRoles } from '@/api/admin'
import { COMMON_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import listPage from '@/mixins/listPage'

export default {
  name: 'SystemAdmins',
  mixins: [listPage],
  data() {
    return {
      COMMON_STATUS,
      username: '',
      visible: false,
      submitting: false,
      roles: [],
      form: { id: null, username: '', realName: '', phone: '', password: '', status: 1, roleIds: [] },
      rules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
      }
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(COMMON_STATUS)
    }
  },
  created() {
    this.loadList()
    this.loadRoles()
  },
  methods: {
    labelOf,
    async loadRoles() {
      try {
        const data = await getRoles()
        this.roles = Array.isArray(data) ? data : (data && data.list) || []
      } catch (e) {
        this.roles = []
      }
    },
    roleNamesOf(row) {
      const ids = row.roleIds || []
      return ids
        .map(id => {
          const role = this.roles.find(r => String(r.id) === String(id))
          return role ? (role.roleName || role.roleCode) : null
        })
        .filter(Boolean)
    },
    fetchList(params) {
      return getAdmins({
        ...params,
        username: this.username || undefined
      })
    },
    openDialog(row) {
      this.form = row
        ? {
          id: row.id,
          username: row.username,
          realName: row.realName || '',
          phone: row.phone || '',
          password: '',
          status: row.status,
          roleIds: (row.roleIds || []).slice()
        }
        : { id: null, username: '', realName: '', phone: '', password: '', status: 1, roleIds: [] }
      this.visible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = { ...this.form }
          if (this.form.id && !payload.password) {
            delete payload.password
          }
          await saveAdmin(payload)
          this.$message.success('保存成功')
          this.visible = false
          this.loadList()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      this.$confirm('确定删除该管理员？', '提示', { type: 'warning' }).then(async () => {
        try {
          await deleteAdmin(row.id)
          this.$message.success('已删除')
          this.loadList()
        } catch (e) {
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.filters {
  display: flex;
  gap: 8px;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
.danger {
  color: #f56c6c;
}
.role-tag {
  margin-right: 4px;
}
.muted {
  color: #909399;
}
.form-tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}
</style>
